import {
  Alert,
  Box,
  Button,
  Card,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  useComputedColorScheme,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { DatePickerInput } from '@mantine/dates';
import { useTranslation } from '@refinedev/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleDashed,
  IconFileText,
  IconUpload,
  IconX,
} from '@tabler/icons-react';

import React, { useState } from 'react';
import { buttonTextColor } from 'utils/buttonUtils';
import { TariffIngestSummary } from './TariffIngestSummary';
import {
  TariffIngestResult,
  BulkTariffIngestJob,
  isBCTariffIngestResult,
} from '@billing/validation';

export interface TariffUploadFiles {
  rate?: File;
  description?: File;
  tariff?: File;
}

export interface TariffUploadSubmission {
  province: 'MB' | 'BC';
  effectiveDate?: string;
  mode: 'VALIDATE' | 'APPLY';
}

interface TariffFileUploadModalProps {
  opened: boolean;
  onClose: () => void;
  tariffFiles: TariffUploadFiles;
  setTariffFiles: (value: TariffUploadFiles) => void;
  handleSubmitFiles: (submission: TariffUploadSubmission) => void;
  uploadResult: TariffIngestResult | null;
  uploadJob: BulkTariffIngestJob | null;
  isUploading: boolean;
}

const getFileName = (file?: File): string => file?.name ?? '';

export const TariffFileUploadModal: React.FC<TariffFileUploadModalProps> = ({
  opened,
  onClose,
  tariffFiles,
  setTariffFiles,
  handleSubmitFiles,
  uploadResult,
  uploadJob,
  isUploading,
}) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');
  const [selectedProvince, setSelectedProvince] = useState<'MB' | 'BC'>('MB');
  const [effectiveDate, setEffectiveDate] = useState('');
  const rateFileName = getFileName(tariffFiles.rate);
  const descriptionFileName = getFileName(tariffFiles.description);
  const tariffFileName = getFileName(tariffFiles.tariff);
  const hasRequiredFiles =
    selectedProvince === 'MB'
      ? Boolean(tariffFiles.rate && tariffFiles.description)
      : Boolean(tariffFiles.tariff && effectiveDate);
  const canSubmit = hasRequiredFiles && !isUploading;
  const bcResult =
    uploadResult && isBCTariffIngestResult(uploadResult) ? uploadResult : null;
  const noFileLabel = translate('tariffs.ingest.noFile', 'No file selected');
  const selectedFileSummary =
    selectedProvince === 'MB'
      ? `${rateFileName || noFileLabel} / ${descriptionFileName || noFileLabel}`
      : tariffFileName || noFileLabel;

  const renderDropzone = ({
    label,
    fileName,
    onDrop,
    testID,
    accept,
  }: {
    label: string;
    fileName: string;
    onDrop: (files: File[]) => void;
    testID: string;
    accept?: string[];
  }) => (
    <Dropzone
      data-testid={testID}
      accept={accept}
      activateOnClick
      multiple={false}
      onDrop={onDrop}
      loading={isUploading}
      radius="md"
      p="lg"
    >
      <Stack align="center" gap="sm">
        <Dropzone.Accept>
          <ThemeIcon size={48} radius="xl" color="green" variant="light">
            <IconCheck size={26} stroke={1.7} />
          </ThemeIcon>
        </Dropzone.Accept>
        <Dropzone.Reject>
          <ThemeIcon size={48} radius="xl" color="red" variant="light">
            <IconX size={26} stroke={1.7} />
          </ThemeIcon>
        </Dropzone.Reject>
        <Dropzone.Idle>
          <ThemeIcon size={48} radius="xl" color="blue" variant="light">
            <IconUpload size={26} stroke={1.7} />
          </ThemeIcon>
        </Dropzone.Idle>
        <Stack align="center" gap={4}>
          <Text fw={600}>{label}</Text>
          <Text size="xs" c="dimmed" ta="center">
            {fileName || translate('tariffs.ingest.noFile', 'No file selected')}
          </Text>
        </Stack>
      </Stack>
    </Dropzone>
  );

  return (
    <Modal
      title={translate('tariffs.ingest.title', 'Bulk Upload Tariffs')}
      opened={opened}
      onClose={onClose}
      size="lg"
      padding="lg"
      data-testid="upload-tariff-modal"
    >
      <Stack gap="md">
        <Select
          label={translate('tariffs.ingest.province', 'Province')}
          data={[
            {
              value: 'MB',
              label: translate('tariffs.ingest.manitoba', 'Manitoba'),
            },
            {
              value: 'BC',
              label: translate(
                'tariffs.ingest.britishColumbia',
                'British Columbia'
              ),
            },
          ]}
          value={selectedProvince}
          onChange={(value) => {
            const province = value === 'BC' ? 'BC' : 'MB';
            setSelectedProvince(province);
            setTariffFiles({});
          }}
          disabled={isUploading}
          required
        />

        {selectedProvince === 'MB' ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {renderDropzone({
              label: translate('tariffs.ingest.rateFile', 'Rate File'),
              fileName: rateFileName,
              testID: 'tariff-rate-dropzone',
              onDrop: (files) =>
                setTariffFiles({ ...tariffFiles, rate: files[0] }),
            })}
            {renderDropzone({
              label: translate(
                'tariffs.ingest.descriptionFile',
                'Description File'
              ),
              fileName: descriptionFileName,
              testID: 'tariff-description-dropzone',
              onDrop: (files) =>
                setTariffFiles({ ...tariffFiles, description: files[0] }),
            })}
          </SimpleGrid>
        ) : (
          <Stack gap="md">
            {renderDropzone({
              label: translate(
                'tariffs.ingest.tariffFile',
                'MSP Fee Schedule File'
              ),
              fileName: tariffFileName,
              testID: 'tariff-bc-dropzone',
              accept: ['text/plain', 'application/octet-stream'],
              onDrop: (files) => setTariffFiles({ tariff: files[0] }),
            })}
            <DatePickerInput
              data-testid="tariff-effective-date"
              label={translate(
                'tariffs.ingest.effectiveDate',
                'Effective date'
              )}
              placeholder={translate(
                'tariffs.ingest.selectEffectiveDate',
                'Select effective date'
              )}
              value={effectiveDate || null}
              onChange={(date) => setEffectiveDate(date ?? '')}
              disabled={isUploading}
              required
              clearable
            />
          </Stack>
        )}

        <Card withBorder radius="md" p="sm">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon
              variant="light"
              color={hasRequiredFiles ? 'blue' : 'gray'}
            >
              <IconFileText size={18} />
            </ThemeIcon>
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Text size="sm" fw={500} truncate>
                {selectedFileSummary}
              </Text>
            </Box>
          </Group>
        </Card>

        {!hasRequiredFiles && (
          <Alert color="blue" icon={<IconAlertTriangle size={16} />}>
            {selectedProvince === 'MB'
              ? translate(
                  'tariffs.ingest.mbRequired',
                  'Select both Manitoba tariff files before submitting.'
                )
              : translate(
                  'tariffs.ingest.bcRequired',
                  'Select a BC fee schedule file and effective date before validating.'
                )}
          </Alert>
        )}
        {uploadJob && !uploadResult && (
          <Alert
            color="blue"
            icon={<IconCircleDashed size={16} />}
            data-testid="tariff-upload-job-status"
          >
            {translate('tariffs.ingest.jobStatus', 'Tariff upload is')}{' '}
            {translate(
              `tariffs.ingest.status.${uploadJob.status}`,
              uploadJob.status.toLowerCase()
            )}
            .
          </Alert>
        )}
        {uploadResult && (
          <Stack gap="md" data-testid="tariff-upload-result">
            <TariffIngestSummary result={uploadResult} />
          </Stack>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {uploadResult
              ? translate('tariffs.ingest.done', 'Done')
              : translate('tariffs.ingest.cancel', 'Cancel')}
          </Button>
          {selectedProvince === 'BC' &&
            (!bcResult || bcResult.mode === 'VALIDATE') && (
              <Button
                variant="light"
                disabled={!canSubmit}
                loading={isUploading}
                onClick={() =>
                  handleSubmitFiles({
                    province: 'BC',
                    effectiveDate,
                    mode: 'VALIDATE',
                  })
                }
              >
                {translate('tariffs.ingest.validate', 'Validate / Preview')}
              </Button>
            )}
          {(!uploadResult ||
            (bcResult?.mode === 'VALIDATE' && bcResult.success)) && (
            <Button
              leftSection={<IconUpload size={16} />}
              disabled={!canSubmit}
              loading={isUploading}
              onClick={() =>
                handleSubmitFiles({
                  province: selectedProvince,
                  effectiveDate:
                    selectedProvince === 'BC' ? effectiveDate : undefined,
                  mode: 'APPLY',
                })
              }
              c={buttonTextColor(computedColorScheme, isUploading)}
            >
              {selectedProvince === 'BC'
                ? translate('tariffs.ingest.apply', 'Apply')
                : translate('tariffs.ingest.submit', 'Submit')}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};
